import styled from '@emotion/styled';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

const JsonPanel = styled(Box)<{
  borderColor: string;
  bg: string;
  text: string;
}>(() => ({
  fontFamily: "Consolas, Menlo, Monaco, 'Courier New', monospace",
  fontSize: 11,
  lineHeight: 1.4,
  borderRadius: 6,
  border: `1px solid var(--borderColor)`,
  backgroundColor: 'var(--bg)',
  color: 'var(--text)',
  padding: 6,
  overflow: 'auto',
  maxHeight: '60vh',
}));

const PanelHeader = styled(Box)<{ headerBg: string }>(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 8px',
  backgroundColor: 'var(--headerBg)',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
}));

const SectionPaper = styled(Paper)<{ outline: string }>(() => ({
  border: `1px solid var(--outline)`,
  borderRadius: 10,
  overflow: 'hidden',
}));

function deepFilterByKey(obj: unknown, term: string): unknown {
  if (!term) return obj;
  if (obj === null || obj === undefined) return obj;

  const lower = term.toLowerCase();

  if (Array.isArray(obj)) {
    const filtered = obj
      .map((item) => deepFilterByKey(item, term))
      .filter((item) => item !== undefined);
    return filtered.length ? filtered : undefined;
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    const kept = entries
      .map(([k, v]) => {
        const child = deepFilterByKey(v, term);
        if (k.toLowerCase().includes(lower) || child !== undefined) {
          return [k, child === undefined ? v : child];
        }
        return undefined;
      })
      .filter(Boolean) as [string, unknown][];
    if (!kept.length) return undefined;
    return kept.reduce(
      (acc, [k, v]) => {
        (acc as Record<string, unknown>)[k] = v;
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return undefined; // primitives only kept if matched at key level
}

function countErrors(obj: unknown): number {
  if (!obj || typeof obj !== 'object') return 0;
  if (Array.isArray(obj))
    return obj.reduce((acc, v) => acc + countErrors(v), 0);
  const o = obj as Record<string, unknown>;
  let total = 0;
  for (const [, v] of Object.entries(o)) {
    if (v && typeof v === 'object') {
      const vo = v as Record<string, unknown>;
      if ('message' in vo || 'type' in vo) {
        total += 1;
      } else {
        total += countErrors(v);
      }
    }
  }
  return total;
}

function collectErrorKeys(obj: unknown, prefix = ''): string[] {
  if (!obj || typeof obj !== 'object') return [];
  const keys: string[] = [];
  const iter = (node: unknown, path: string) => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach((n, idx) => iter(n, `${path}[${idx}]`));
      return;
    }
    const o = node as Record<string, unknown>;
    if ('message' in o || 'type' in o) {
      keys.push(path);
      return;
    }
    for (const [k, v] of Object.entries(o)) {
      iter(v, path ? `${path}.${k}` : k);
    }
  };
  iter(obj, prefix);
  return keys;
}

const RHFDebugger = () => {
  const { watch, formState, reset } = useFormContext();
  const theme = useTheme();
  const liveValues = watch();
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState('');

  const filteredValues = useMemo(() => {
    const src = liveValues;
    const out = deepFilterByKey(src, filter);
    return (out ?? src) as Record<string, unknown>;
  }, [liveValues, filter]);

  const errors = useMemo(() => formState?.errors ?? {}, [formState?.errors]);
  const filteredErrors = useMemo(() => {
    const out = deepFilterByKey(errors, filter);
    return (out ?? errors) as Record<string, unknown>;
  }, [errors, filter]);

  const errorCount = useMemo(() => countErrors(errors), [errors]);
  const fieldCount = useMemo(
    () =>
      liveValues && typeof liveValues === 'object'
        ? Object.keys(liveValues as Record<string, unknown>).length
        : 0,
    [liveValues]
  );
  const errorKeysPreview = useMemo(
    () => collectErrorKeys(errors).slice(0, 6),
    [errors]
  );

  const copyToClipboard = async (payload: unknown) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      // no-op
    }
  };

  return (
    <Box
      role='complementary'
      sx={{
        width: expanded ? '100%' : 28,
        minWidth: 28,
        position: 'sticky',
        top: 8,
        alignSelf: 'flex-start',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      {!expanded && (
        <SectionPaper
          elevation={0}
          style={{
            // @ts-ignore CSS var assignment
            '--outline': theme.palette.divider,
          }}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
            }}
          >
            <Tooltip
              title={`Open Debugger\nErrors: ${errorCount}\nFields: ${fieldCount}`}
            >
              <IconButton
                size='small'
                onClick={() => setExpanded(true)}
                aria-label='Expand Debugger'
              >
                <ExpandMoreIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        </SectionPaper>
      )}

      {expanded && (
        <Stack spacing={1}>
          <SectionPaper
            elevation={0}
            style={{
              // @ts-ignore CSS var assignment
              '--outline': theme.palette.divider,
            }}
          >
            <PanelHeader
              style={{
                // @ts-ignore CSS var assignment
                '--headerBg': theme.palette.action.hover,
              }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <IconButton
                  size='small'
                  onClick={() => setExpanded(false)}
                  aria-label='Collapse'
                >
                  <ExpandLessIcon fontSize='small' />
                </IconButton>
                <Typography variant='overline' color='text.secondary'>
                  Debugger
                </Typography>
                <Tooltip
                  title={
                    errorKeysPreview.length
                      ? `Errors: ${errorCount}\nFields: ${fieldCount}\nError fields: ${errorKeysPreview.join(
                          ', '
                        )}`
                      : `Errors: ${errorCount}\nFields: ${fieldCount}`
                  }
                >
                  <Typography variant='caption' color='text.secondary'>
                    {`Errors ${errorCount} • Fields ${fieldCount}`}
                  </Typography>
                </Tooltip>
              </Stack>
              <Stack direction='row' spacing={1} alignItems='center'>
                <TextField
                  size='small'
                  placeholder='Filter keys...'
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <Tooltip title='Reset form'>
                  <IconButton
                    size='small'
                    aria-label='Reset form'
                    onClick={() => {
                      reset();
                      setFilter('');
                    }}
                  >
                    <RestartAltIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
            </PanelHeader>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                flexWrap='wrap'
              >
                <ChipStat label='isDirty' value={formState.isDirty} />
                <ChipStat label='isValid' value={formState.isValid} />
                <ChipStat label='isSubmitting' value={formState.isSubmitting} />
                <ChipStat label='submitCount' value={formState.submitCount} />
              </Stack>
            </Box>
          </SectionPaper>

          <Stack direction='row' spacing={1}>
            <SectionPaper
              elevation={0}
              sx={{ flex: 1 }}
              style={{
                // @ts-ignore CSS var assignment
                '--outline': theme.palette.divider,
              }}
            >
              <Box sx={{ p: 1 }}>
                <Stack
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Values
                  </Typography>
                  <Tooltip title='Copy JSON'>
                    <IconButton
                      aria-label='copy-values'
                      onClick={() => copyToClipboard(filteredValues)}
                      size='small'
                    >
                      <ContentCopyIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <JsonPanel
                  component='pre'
                  role='region'
                  aria-label='Form values'
                  style={{
                    // @ts-ignore CSS var assignment
                    '--borderColor': theme.palette.divider,
                    // @ts-ignore CSS var assignment
                    '--bg': theme.palette.background.paper,
                    // @ts-ignore CSS var assignment
                    '--text': theme.palette.text.primary,
                  }}
                >
                  {JSON.stringify(filteredValues, null, 2)}
                </JsonPanel>
              </Box>
            </SectionPaper>

            <SectionPaper
              elevation={0}
              sx={{ flex: 1 }}
              style={{
                // @ts-ignore CSS var assignment
                '--outline': theme.palette.divider,
              }}
            >
              <Box sx={{ p: 1 }}>
                <Stack
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Errors
                  </Typography>
                  <Tooltip title='Copy JSON'>
                    <IconButton
                      aria-label='copy-errors'
                      onClick={() => copyToClipboard(filteredErrors)}
                      size='small'
                    >
                      <ContentCopyIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <JsonPanel
                  component='pre'
                  role='region'
                  aria-label='Form errors'
                  style={{
                    // @ts-ignore CSS var assignment
                    '--borderColor': theme.palette.divider,
                    // @ts-ignore CSS var assignment
                    '--bg': theme.palette.background.paper,
                    // @ts-ignore CSS var assignment
                    '--text': theme.palette.error.main,
                  }}
                >
                  {JSON.stringify(filteredErrors, null, 2)}
                </JsonPanel>
              </Box>
            </SectionPaper>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const ChipStat = ({ label, value }: { label: string; value: unknown }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 999,
        padding: '4px 10px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='caption' color='text.primary'>
        {String(value)}
      </Typography>
    </Box>
  );
};

export default RHFDebugger;
